import { Component, Input } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { EpicService } from "../../services/epic.service";
import { ContentService } from "../../services/content.service";
import {
  DonorProfile,
  RecipientProfile,
} from "../../models/donorProfile.model";
import { NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";

@Component({
  selector: "app-epic-lookup-modal",
  templateUrl: "./epic-lookup-modal.component.html",
  styleUrls: ["./epic-lookup-modal.component.scss"],
})
export class EpicLookupModalComponent {
  isLoading = false;
  isSaving = false;
  submitted = false;
  patientSearch = {
    firstName: "",
    lastName: "",
    dateOfBirth: "",
  };
  practitionerSearch = {
    fullName: "",
    npi: "",
  };
  searchResults: any[] = [];
  successMessage: string | null = null;
  countdown = 0;
  countdownInterval: any;

  @Input() donorUserId?: string;
  @Input() userJourneySectionId?: string;
  @Input() linkId?: string;
  @Input() type!: "Donor" | "Recipient" | "Provider";

  constructor(
    public activeModal: NgbActiveModal,
    private epicService: EpicService,
    private contentService: ContentService
  ) {}

  hasResults(): boolean {
    return (
      this.searchResults?.length > 0 ||
      (this.submitted && this.searchResults.length == 0)
    );
  }

  formatDateToMMDDYYYY(dateStr: string | undefined): string {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${month}-${day}-${year}`;
  }

  close() {
    this.activeModal.dismiss();
  }

  sanitizeDateInput(dateInput: NgbDateStruct | string | undefined): string {
    if (!dateInput) return "";

    if (typeof dateInput === "object") {
      const dobObj = dateInput as NgbDateStruct;
      return `${dobObj.year}-${String(dobObj.month).padStart(2, "0")}-${String(dobObj.day).padStart(2, "0")}`;
    } else if (typeof dateInput === "string") {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
        // Already yyyy-mm-dd
        return dateInput;
      } else if (/^\d{2}[\/-]\d{2}[\/-]\d{4}$/.test(dateInput)) {
        const parts = dateInput.split(/[\/-]/);
        return `${parts[2]}-${parts[0].padStart(2, "0")}-${parts[1].padStart(2, "0")}`;
      } else {
        return dateInput; // fallback
      }
    }
    return "";
  }

  search() {
    if (
      (this.type === "Provider" && !this.practitionerSearch.fullName) ||
      (this.type !== "Provider" &&
        (!this.patientSearch.firstName ||
          !this.patientSearch.lastName ||
          !this.patientSearch.dateOfBirth))
    ) {
      console.log("Search not triggered: missing required fields.");
      return;
    } else {
      this.submitted = true;
      this.isLoading = true;
      let dateString = "";
      if (
        this.patientSearch.dateOfBirth &&
        typeof this.patientSearch.dateOfBirth === "object"
      ) {
        dateString = this.sanitizeDateInput(this.patientSearch.dateOfBirth);
      }

      const searchObservable =
        this.type === "Provider"
          ? this.epicService.searchProviders(
              `${this.practitionerSearch.fullName}`
            )
          : this.epicService.searchPatients(
              this.patientSearch.firstName,
              this.patientSearch.lastName,
              dateString
            );

      searchObservable.subscribe({
        next: (value) => {
          this.searchResults = value;
          this.isLoading = false;
        },
        error: (err) => {
          console.log("err: ", err);
          this.isLoading = false;
        },
      });
    }
  }

  selectRecord(epicRecordData: any) {
    this.isSaving = true;

    if (this.type === "Provider") {
      if (epicRecordData.id && this.userJourneySectionId) {
        if (this.linkId) {
          this.contentService
            .updateProviderInSection(
              this.linkId,
              epicRecordData.id,
              this.userJourneySectionId
            )
            .subscribe({
              next: (response) => {
                this.isSaving = false;
                this.showSuccessAndClose({
                  epicRecord: epicRecordData,
                  response,
                });
              },
              error: (err) => {
                console.log(err);
                console.log("provider update failed");
                this.isSaving = false;
              },
            });
        } else {
          this.contentService
            .addProviderToSection(epicRecordData.id, this.userJourneySectionId)
            .subscribe({
              next: (response) => {
                this.isSaving = false;
                this.showSuccessAndClose({
                  epicRecord: epicRecordData,
                  response,
                });
              },
              error: (err) => {
                console.log(err);
                console.log("provider update failed");
                this.isSaving = false;
              },
            });
        }
      }
    }

    if (this.type == "Donor") {
      const donorProfile: DonorProfile = {
        epicPatientId: epicRecordData.id,
        firstName: epicRecordData.firstName,
        lastName: epicRecordData.lastName,
        dateOfBirth: epicRecordData.dateOfBirth,
      };

      if (this.donorUserId) {
        this.contentService
          .updateDonorProfileInfoByAdmin(this.donorUserId, donorProfile)
          .subscribe({
            next: (response) => {
              this.isSaving = false;
              this.showSuccessAndClose(response);
            },
            error: (err) => {
              console.log("profile update failed");
              this.isSaving = false;
            },
          });
      }
    } else if (this.type == "Recipient") {
      const recipientProfile: RecipientProfile = {
        epicPatientId: epicRecordData.id,
        donorUserId: this.donorUserId,
        firstName: epicRecordData.firstName,
        lastName: epicRecordData.lastName,
        dateOfBirth: epicRecordData.dateOfBirth,
      };

      if (this.donorUserId) {
        this.contentService
          .updateRecipientProfileInfoByAdmin(recipientProfile)
          .subscribe({
            next: (response) => {
              this.isSaving = false;
              this.showSuccessAndClose(response);
            },
            error: (err) => {
              console.log("profile update failed");
              this.isSaving = false;
            },
          });
      }
    }
  }

  /**
   * Show success message and countdown before closing the modal.
   */
  showSuccessAndClose(response: any) {
    this.successMessage = `${this.type === "Provider" ? "Provider" : "Patient record"} linked successfully!`;
    this.countdown = 3;

    this.countdownInterval = setInterval(() => {
      this.countdown -= 1;
      if (this.countdown === 0) {
        clearInterval(this.countdownInterval);
        this.activeModal.close(response);
      }
    }, 1000);
  }

  isDateOfBirthInvalid(): boolean {
    if (!this.patientSearch.dateOfBirth) {
      return false;
    }

    const enteredDate = new Date(this.patientSearch.dateOfBirth);
    const minDate = new Date("1900-01-01");
    const maxDate = new Date();

    return enteredDate < minDate || enteredDate > maxDate;
  }

  getPhoneNumberString(phone: any): string {
    if (!phone) return "";
    return phone instanceof Array ? phone.join(", ") : phone;
  }
}
